import z, { ZodError, ZodIssue, ZodIssueCode } from 'zod';

type TraversableValue = string | number | boolean | TraversableObject;
export type InferFromSchema<T extends z.ZodSchema<any>> = z.infer<T>;

interface TraversableObject {
  [key: string | number]: TraversableValue;
}

/**
 * List of error codes for errors related to a value in a object not matching a
 * ruleset. These are the errors we want to detect and transform to include the
 * failing value.
 * See:
 * https://github.com/colinhacks/zod/blob/master/ERROR_HANDLING.md
 */
const VALUELESS_ERRORS: ZodIssueCode[] = [
  'invalid_literal',
  'invalid_union_discriminator',
  'invalid_enum_value',
  'invalid_date',
  'invalid_string',
];

function traverse(path: Array<string | number>, obj: TraversableObject) {
  while (path.length > 0) {
    // Assume the path zod provides is valid.
    obj = obj[path.shift()!] as TraversableObject;
  }
  return obj;
}

function extractValue(path: Array<string | number>, inputObject: unknown) {
  try {
    return ` (value: ${traverse(path, inputObject as TraversableObject)})`;
  } catch (err: unknown) {
    return ` (Could not extract value: ${(err as Error).message})`;
  }
}

function annotateZodIssues(issues: ZodIssue[], inputObject: TraversableObject) {
  for (const issue of issues) {
    // When zod finds a field that fails to match some ruleset it
    // won't include the failing value in the error message.
    // See https://github.com/colinhacks/zod/issues/461
    // We add it in here to help with debugging and because we are sure
    // this is not sensitive PII.
    if (issue.code === 'invalid_union') {
      // `invalid_union` errors are special in that they only wrap a list of
      // other errors.
      issue.unionErrors.map((unionError: ZodError) =>
        annotateZodIssues(unionError.issues, inputObject),
      );
    } else if (VALUELESS_ERRORS.includes(issue.code)) {
      issue.message += extractValue(issue.path, inputObject);
    }
  }
}

/** Declares a type that can be anything except a Promise. */
type NonPromise<T> = T extends Promise<any> ? never : T;

/**
 * Attempt to parse a object with a given zod schema. If an error occurs,
 * attempt to add more helpful information to improve debug experience before
 * throwing the error.
 */
export function zodParse<T extends z.ZodType<any, z.ZodTypeDef, any>, V>(
  schema: T,
  // Ensure that the input object is not a promise as a saftey guard against
  // code that may forget to await a object before trying to parse it.
  inputObject: NonPromise<V>,
): InferFromSchema<T> {
  try {
    return schema.parse(inputObject);
  } catch (error: unknown) {
    if (error instanceof ZodError) {
      annotateZodIssues(error.issues, inputObject as TraversableObject);
      throw new Error(JSON.stringify(error.issues));
    } else {
      throw error;
    }
  }
}
